"use client";

import { c as _c } from "react/compiler-runtime";
import ErrorComponent from "@/components/GlobalError";
import { Toaster, TooltipProvider } from "@/design-system";
import { clerkAllowedRedirectOrigins } from "@/utils/clerk-redirect-origins";
import { normalizeClientRouteFamily } from "@/utils/client-route-family";
import { applyClientSentryAttribution, applyClientSentryAttributionToScope } from "@/utils/client-sentry-attribution";
import type { ClerkHostOptions } from "@/utils/workspace-hosts";
import { ClerkFailed, ClerkProvider } from "@clerk/nextjs";
import { ErrorBoundary } from "@sentry/nextjs";
import dynamic from "next/dynamic";
import { unstable_rethrow, usePathname } from "next/navigation";
import React, { useEffect } from "react";
import { toast } from "sonner";
import "./globals.css";
import { TimeAgoProvider } from "@/utils/date.client";
import { TRPCProvider } from "./providers";

// Modal focus isolation can modify a streamed progress node before hydration.
// This client-owned indicator has no server-visible content to preserve.
const NavigationFeedback = dynamic(() => import("@/components/navigation/NavigationFeedback").then(module => module.NavigationFeedback), {
  ssr: false
});
function ClerkLoadRecovery() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp4, t0);
  return null;
}
function _temp4() {
  toast.error("Sign-in controls couldn't load", {
    id: "clerk-load-failure",
    description: "Save your work before reloading to reconnect.",
    duration: Infinity,
    classNames: {
      actionButton: "min-h-11",
      cancelButton: "min-h-11"
    },
    action: {
      label: "Reload",
      onClick: _temp
    },
    cancel: {
      label: "Dismiss",
      onClick: _temp2
    }
  });
  return _temp3;
}
function _temp3() {
  toast.dismiss("clerk-load-failure");
}
function _temp2() {
  return toast.dismiss("clerk-load-failure");
}
function _temp() {
  return window.location.reload();
}
export default function RootLayoutClient(t0) {
  const $ = _c(12);
  const {
    children,
    clerkHostOptions
  } = t0;
  const pathname = usePathname();
  let t1;
  let t2;
  if ($[0] !== pathname) {
    t1 = () => {
      applyClientSentryAttribution({
        route: pathname,
        routeFamily: normalizeClientRouteFamily(pathname)
      });
    };
    t2 = [pathname];
    $[0] = pathname;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <React.Suspense fallback={null}><NavigationFeedback /></React.Suspense>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ErrorComponent />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Toaster />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ClerkFailed><ClerkLoadRecovery /></ClerkFailed>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== children) {
    t7 = <TRPCProvider>{t5}{t6}{children}</TRPCProvider>;
    $[7] = children;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== clerkHostOptions || $[10] !== t7) {
    t8 = <TooltipProvider>{t3}<ErrorBoundary fallback={t4} beforeCapture={_temp5}><TimeAgoProvider><ClerkProvider allowedRedirectOrigins={clerkAllowedRedirectOrigins} {...clerkHostOptions}>{t7}</ClerkProvider></TimeAgoProvider></ErrorBoundary></TooltipProvider>;
    $[9] = clerkHostOptions;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  return t8;
}
function _temp5(scope, error) {
  unstable_rethrow(error);
  applyClientSentryAttributionToScope(scope, {
    errorSource: "client_render"
  });
}